import { useState } from "react";
import { Download, Check, Loader2, CheckCircle2 } from "lucide-react";
import { CTAButton } from "../shared/CTAButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
const LeadFormClean = () => {
  const {
    toast
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    practice: "",
    phone: ""
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUploadProgress(0);

    // Schritt 1: Validierung
    setCurrentStep("Daten werden validiert...");
    setUploadProgress(25);
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // Schritt 2: Speichern
    setCurrentStep("Daten werden gespeichert...");
    setUploadProgress(60);
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Schritt 3: Download vorbereiten
    setCurrentStep("Download wird vorbereitet...");
    setUploadProgress(85);
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // Schritt 4: Abschluss
    setUploadProgress(100);
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Trigger download - browser-friendly method
    window.location.href = '/downloads/cyber-checkliste-arztpraxen.pdf';
    
    toast({
      title: "Download startet!",
      description: "Falls nicht, nutzen Sie den Download-Link unten."
    });
    
    setIsSuccess(true);
    setIsSubmitting(false);
  };
  const trustBadges = ["Kostenlos", "Keine Anmeldung", "Sofortiger Download", "DSGVO-konform"];
  
  // Success State
  if (isSuccess) {
    return (
      <section id="lead-form" className="py-16 sm:py-20 lg:py-24">
        <div className="container max-w-3xl mx-auto px-6 sm:px-8">
          <div className="border border-white/10 rounded-xl p-8 sm:p-10 backdrop-blur-sm text-center">
            {/* Animated Checkmark */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/20 flex items-center justify-center animate-scale-in">
              <CheckCircle2 className="w-12 h-12 text-accent animate-fade-in" strokeWidth={2.5} />
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-bold mb-3 animate-fade-in">
              Download erfolgreich gestartet!
            </h3>
            
            <p className="text-base sm:text-lg text-muted-foreground mb-6 animate-fade-in">
              Ihre Cyber-Checkliste wird heruntergeladen.
            </p>
            
            {/* Manual Download Link */}
            <a 
              href="/downloads/cyber-checkliste-arztpraxen.pdf" 
              download="cyber-checkliste-arztpraxen.pdf"
              className="inline-flex items-center gap-2 text-accent hover:underline font-medium text-base mb-4 animate-fade-in hover-scale"
            >
              <Download className="w-5 h-5" />
              Download startet nicht? Hier klicken
            </a>
            
            <p className="text-sm text-muted-foreground mt-4 animate-fade-in">
              Prüfen Sie auch Ihren Download-Ordner.
            </p>
          </div>
        </div>
      </section>
    );
  }
  
  return <section id="lead-form" className="py-16 sm:py-20 lg:py-24">
      <div className="container max-w-3xl mx-auto px-6 sm:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 border border-accent/30 px-4 py-2 rounded-full mb-4">
            <Download className="w-4 h-4 text-accent" />
            <span className="text-xs font-semibold text-accent uppercase tracking-wide">KOSTENLOSER DOWNLOAD</span>
          </div>
          
          <h2 className="text-2xl lg:text-4xl font-bold mb-4 leading-tight sm:text-3xl">
            Checkliste jetzt <span className="text-accent">kostenlos</span> sichern
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground">
            13 Sofortmaßnahmen für maximalen Schutz Ihrer Praxis
          </p>
        </div>
        
        {/* Form Card - TRANSPARENT */}
        <div className="border border-white/10 rounded-xl p-8 sm:p-10 backdrop-blur-sm relative">
          
          {/* Progress Overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center z-50 rounded-xl animate-fade-in">
              <div className="w-full max-w-md px-8">
                <Progress 
                  value={uploadProgress} 
                  className="h-3 mb-4"
                />
                <p className="text-center text-sm text-muted-foreground animate-pulse">
                  {currentStep}
                </p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Vor- und Nachname *
              </Label>
              <Input id="name" required value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="h-12 bg-transparent border-white/20 focus:border-accent" placeholder="Dr. Max Mustermann" />
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                E-Mail-Adresse *
              </Label>
              <Input id="email" type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="h-12 bg-transparent border-white/20 focus:border-accent" placeholder="praxis@beispiel.de" />
            </div>
            
            {/* Praxistyp */}
            <div className="space-y-2">
              <Label htmlFor="practice" className="text-sm font-medium">
                Praxistyp *
              </Label>
              <Select required value={formData.practice} onValueChange={value => setFormData({
              ...formData,
              practice: value
            })}>
                <SelectTrigger className="h-12 bg-transparent border-white/20 focus:border-accent">
                  <SelectValue placeholder="Bitte auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hausarzt">Hausarztpraxis</SelectItem>
                  <SelectItem value="facharzt">Facharztpraxis</SelectItem>
                  <SelectItem value="zahnarzt">Zahnarztpraxis</SelectItem>
                  <SelectItem value="mvz">MVZ</SelectItem>
                  <SelectItem value="klinik">Klinik</SelectItem>
                  <SelectItem value="andere">Andere</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Phone (optional) */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                Telefon <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input id="phone" type="tel" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="h-12 bg-transparent border-white/20 focus:border-accent" placeholder="+49 123 456789" />
            </div>
            
            {/* Submit Button */}
            <CTAButton type="submit" icon={isSubmitting ? Loader2 : Download} className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Wird versendet..." : "Jetzt herunterladen"}
            </CTAButton>
          </form>
          
          {/* Trust Badges */}
          <div className="mt-8 flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
            {trustBadges.map((badge, idx) => <div key={idx} className="flex items-center gap-2">
                <Check className="w-3 h-3 text-accent" />
                <span>{badge}</span>
              </div>)}
          </div>
          
          {/* Manual Download Link */}
          <div className="mt-6 text-center">
            <a 
              href="/downloads/cyber-checkliste-arztpraxen.pdf" 
              download="cyber-checkliste-arztpraxen.pdf"
              className="text-sm text-accent hover:underline inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download startet nicht? Hier klicken
            </a>
          </div>
        </div>
      </div>
    </section>;
};
export default LeadFormClean;